/**
 * Chip-dropdown do período de ENTRADA NA ETAPA (barra de filtros do CRM).
 *
 * Presets (hoje/semana/mês/ano) + intervalo escolhido a dedo. O backend
 * recebe sempre um par de datas locais `entrou_de`/`entrou_ate`, então os
 * presets são só atalhos para pares comuns — e qualquer um dos lados pode
 * ficar aberto ("a partir de 01/08").
 *
 * É GLOBAL de propósito: cada lead está em exatamente uma coluna, e comparar
 * colunas ("12 entraram em Contato e 3 em Negociação esta semana") só faz
 * sentido com a mesma janela. Janela por coluna também seria estado
 * invisível — uma coluna filtrada parece vazia e o funil é lido errado.
 *
 * O mesmo conjunto de presets aparece no menu "•••" de cada coluna
 * (EtapaColuna), que chama este mesmo `onChange`.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiCalendar, FiCheck, FiChevronDown } from "react-icons/fi";

import { MenuItem, MenuList, MenuWrapper } from "./styles";
import {
  PERIODOS_ENTRADA,
  PERIODO_PERSONALIZADO,
  intervaloDoPeriodo,
  intervaloInvertido,
  labelDoPeriodo,
  rotuloIntervaloLivre,
} from "./periodoEntrada";

export default function PeriodoEntradaFiltro({
  valor,          // preset ativo ("", "hoje", ..., "personalizado")
  de = "",
  ate = "",
  onChange,       // (preset) — presets resolvem o intervalo no board
  onIntervalo,    // ({de, ate}) — intervalo escolhido a dedo
  aberto,
  onAbertoChange,
}) {
  const [abertoLocal, setAbertoLocal] = useState(false);
  const controlado = typeof aberto === "boolean";
  const estaAberto = controlado ? aberto : abertoLocal;
  const setAberto = (v) => {
    if (controlado) onAbertoChange?.(v);
    else setAbertoLocal(v);
  };
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!estaAberto) return undefined;
    const fora = (e) => {
      if (!wrapRef.current?.contains(e.target)) setAberto(false);
    };
    const esc = (e) => {
      if (e.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estaAberto]);

  const ativo = Boolean(valor);
  const personalizado = valor === PERIODO_PERSONALIZADO;
  const invertido = intervaloInvertido(de, ate);
  // Com preset, o intervalo mostrado é o resolvido agora; com datas a dedo,
  // são as próprias datas.
  const doPreset = intervaloDoPeriodo(valor);
  const intervaloAtual = personalizado
    ? rotuloIntervaloLivre(de, ate)
    : rotuloIntervaloLivre(doPreset.de, doPreset.ate);

  const rotuloBotao = personalizado
    ? (rotuloIntervaloLivre(de, ate) || "Período escolhido")
    : (ativo ? labelDoPeriodo(valor) : "Entrada na etapa");

  return (
    <MenuWrapper ref={wrapRef}>
      <button
        type="button"
        className={"btn btn--sm " + (ativo ? "btn--primary" : "btn--secondary")}
        onClick={() => setAberto(!estaAberto)}
        aria-haspopup="menu"
        aria-expanded={estaAberto}
        title={
          ativo
            ? `Leads que entraram na etapa em ${intervaloAtual}`
            : "Filtrar por quando o lead entrou na etapa"
        }
      >
        <FiCalendar size={14} aria-hidden="true" />
        {rotuloBotao}
        <FiChevronDown size={13} aria-hidden="true" />
      </button>
      {estaAberto && (
        <MenuList role="menu" style={{ minWidth: 250 }}>
          <li className="menu-titulo">Entrou na etapa em</li>
          {PERIODOS_ENTRADA.map((p) => (
            <MenuItem key={p.id || "todos"} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={valor === p.id}
                onClick={() => {
                  setAberto(false);
                  onChange(p.id);
                }}
              >
                <FiCheck
                  size={13}
                  aria-hidden="true"
                  style={{ opacity: valor === p.id ? 1 : 0 }}
                />
                {p.label}
              </button>
            </MenuItem>
          ))}

          <li className="menu-sep" role="separator" />
          <li className="menu-titulo">Intervalo específico</li>
          {/* Aplicação ao vivo (sem botão "Aplicar"): o quadro responde a
              cada campo, e deixar um lado vazio é intencional — "a partir
              de 01/08" é um filtro legítimo. */}
          <li className="menu-datas">
            <label>
              De
              <input
                type="date"
                value={de}
                max={ate || undefined}
                onChange={(e) => onIntervalo({ de: e.target.value, ate })}
              />
            </label>
            <label>
              Até
              <input
                type="date"
                value={ate}
                min={de || undefined}
                onChange={(e) => onIntervalo({ de, ate: e.target.value })}
              />
            </label>
          </li>
          {invertido && (
            <li className="menu-nota" style={{ color: "var(--danger, #dc2626)" }}>
              A data inicial é depois da final — nenhum lead se encaixa.
            </li>
          )}
          {ativo && !invertido && intervaloAtual && (
            <li className="menu-nota">{intervaloAtual}</li>
          )}
          {ativo && (
            <MenuItem role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAberto(false);
                  onChange("");
                }}
              >
                Limpar período
              </button>
            </MenuItem>
          )}
        </MenuList>
      )}
    </MenuWrapper>
  );
}
